import React, { useMemo } from 'react';
import { deriveUsageLines } from '../lib/emissions';
import { standardizeUsageLines } from '../lib/uom';
import { ArrowRight, Ruler } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * Shows how the bill's as-billed usage converts to the standard reporting unit
 * (Electricity→kWh, Gas→therms, Water→gallons). Computed LIVE from the bill's
 * own line-items so reviewers can trust the normalized figures. Renders nothing
 * when no recognizable usage lines exist.
 */
export function StandardizedUsageCard({ commodity, usageDetails, meterReads, accounts, className = '' }) {
  const rows = useMemo(
    () => standardizeUsageLines(deriveUsageLines({
      commodity, usage_details: usageDetails, meter_reads: meterReads, accounts,
    })),
    [commodity, usageDetails, meterReads, accounts],
  );

  if (!rows.length) return null;

  return (
    <div className={`border border-border bg-slate-50 p-3 ${className}`} data-testid="std-usage-panel">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        <Ruler className="h-3.5 w-3.5" /> Standardized usage
        <span className="normal-case tracking-normal text-slate-400">· as-billed → reporting unit</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div
            key={r.bucket || i}
            className="flex items-center justify-between text-xs"
            data-testid={`std-usage-row-${r.bucket}`}
          >
            <span className="font-medium capitalize text-slate-700">{r.commodity}</span>
            <div className="flex items-center gap-2 font-mono">
              <span className="text-slate-600">
                {fmt(r.origQty)} <span className="text-slate-400">{r.origUom}</span>
              </span>
              <ArrowRight className="h-3 w-3 text-slate-400" />
              {r.stdQty != null ? (
                <span className="font-semibold text-slate-900">
                  {fmt(r.stdQty)} <span className="text-slate-500">{r.stdUom}</span>
                </span>
              ) : (
                <span className="text-amber-600" data-testid={`std-usage-unknown-${r.bucket}`}>
                  unrecognized unit ({r.origUom})
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StandardizedUsageCard;
